import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { abuseIpdbActivityQuery, type AbuseIpdbCheck } from '../../api/settingsAbuseIpdb'
import { Spinner } from '../../ui/Spinner'

/**
 * "Activity" sub setarea AbuseIPDB: ce a verificat si ce a blocat ph4 in
 * ultimele 30 de zile, in firma selectata. Implicit numai blocarile.
 */

const OUTCOME: Record<string, string> = {
  noted: 'Noted only',
  below: 'Below 20%',
  allowlisted: 'On the never-block list',
  own_ip: "The company's own IP",
  not_public: 'Not a public IP',
  no_ip: 'No attacker IP in the event',
  already: 'Already blocked on this device',
  paused: 'Paused (daily AbuseIPDB limit)',
}

/** Durata unei trepte: 10 min, 30 min, 30 days. */
export function blockTime(minutes: number): string {
  if (minutes % 1440 === 0) return minutes === 1440 ? '1 day' : `${minutes / 1440} days`
  if (minutes % 60 === 0) return `${minutes / 60} h`
  return `${minutes} min`
}

function result(r: AbuseIpdbCheck): { cls: string; text: string; title?: string } {
  if (r.outcome === 'gateway') {
    return {
      cls: 'label-warning',
      text: `Block on the VPN gateway${r.block_minutes ? `: ${blockTime(r.block_minutes)}` : ''}`,
      title: 'RADIUS / VPN: the logons reach the NPS server through the VPN gateway, so a block on the NPS server would not stop it. Block this IP on the gateway (FortiGate).',
    }
  }
  if (r.outcome === 'would_block') return { cls: 'label-warning', text: `Would block ${r.block_minutes ? blockTime(r.block_minutes) : ''} (watch only)` }
  if (r.outcome === 'blocked') {
    if (r.state === 'active') return { cls: 'label-danger', text: r.block_minutes ? `Blocked ${blockTime(r.block_minutes)}, until ${r.unblock_due}` : `Blocked until ${r.unblock_due}` }
    if (r.state === 'auto_unblocked') return { cls: 'label-default', text: `Unblocked automatically ${r.unblocked_at}` }
    if (r.state === 'hand_unblocked') return { cls: 'label-default', text: `Unblocked by a person ${r.unblocked_at}` }
    return { cls: 'label-danger', text: 'Blocked' }
  }
  if (r.outcome === 'error') return { cls: 'label-warning', text: `Error: ${r.error ?? 'no answer'}` }
  return { cls: 'label-info', text: OUTCOME[r.outcome] ?? r.outcome }
}

export function AbuseIpdbActivity() {
  const [all, setAll] = useState(false)
  const q = useQuery(abuseIpdbActivityQuery(all))
  const data = q.data

  return (
    <section className="abuseipdb-activity" aria-labelledby="abuseipdb-activity-title">
      <div className="abuseipdb-activity-head">
        <h5 id="abuseipdb-activity-title">
          <strong>Activity</strong> <span className="text-muted">(last {data?.days ?? 30} days, UTC)</span>
        </h5>
        <div className="btn-group btn-group-sm" role="group" aria-label="Show">
          <button type="button" className={'btn ' + (all ? 'btn-default' : 'btn-primary')} aria-pressed={!all} onClick={() => setAll(false)}>
            Blocked
          </button>
          <button type="button" className={'btn ' + (all ? 'btn-primary' : 'btn-default')} aria-pressed={all} onClick={() => setAll(true)}>
            All checks
          </button>
        </div>
      </div>
      {q.isError ? <p className="text-danger">{q.error instanceof Error ? q.error.message : 'The activity could not be read.'}</p> : null}
      {!data && !q.isError ? <Spinner /> : null}
      {data ? (
        <>
          <p className="abuseipdb-counts">
            {[
              `${data.counts.blocked} blocked`,
              data.counts.would_block ? `${data.counts.would_block} would block` : null,
              data.counts.gateway ? `${data.counts.gateway} to block on the VPN gateway` : null,
              data.counts.noted ? `${data.counts.noted} noted` : null,
              `${data.counts.checked} checked`,
              data.counts.internal ? `${data.counts.internal} skipped (internal or missing address)` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {data.rows.length === 0 ? (
            <p className="help-block">{all ? 'No checks in the last 30 days.' : 'Nothing was blocked in the last 30 days.'}</p>
          ) : (
            <div className="abuseipdb-activity-scroll">
              <table className="table table-condensed abuseipdb-activity-table">
                <thead>
                  <tr>
                    <th scope="col">Time</th>
                    <th scope="col">Device</th>
                    <th scope="col">Attacker IP</th>
                    <th scope="col">Score</th>
                    <th scope="col">Result</th>
                    <th scope="col">Open</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => {
                    const res = result(r)
                    return (
                      <tr key={r.id}>
                        <td className="abuseipdb-nowrap">{r.checked_at}</td>
                        <td className="abuseipdb-nowrap">{r.device_name || `#${r.device_id}`}</td>
                        <td className="abuseipdb-nowrap">
                          {r.ip ? (
                            <a href={`https://www.abuseipdb.com/check/${r.ip}`} target="_blank" rel="noopener noreferrer" title="Open in AbuseIPDB">
                              <span className="abuseipdb-ip">{r.ip}</span>
                            </a>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="abuseipdb-nowrap">
                          {r.score !== null ? `${r.score}%` : '-'}
                          {r.country ? <span className="text-muted"> {r.country}</span> : null}
                        </td>
                        <td>
                          <span className={'label ' + res.cls} title={res.title}>
                            {res.text}
                          </span>
                          {(r.seen ?? 1) > 1 ? (
                            <span
                              className="label label-default abuseipdb-seen"
                              title={`Seen ${r.seen} times on this device within 24 hours of the check${r.last_seen_at ? `; last at ${r.last_seen_at} UTC` : ''}`}
                            >
                              seen {r.seen}×
                            </span>
                          ) : null}
                          {r.reported_at ? (
                            <span className="label label-default abuseipdb-reported" title={`Reported to AbuseIPDB ${r.reported_at} UTC`}>
                              Reported
                            </span>
                          ) : null}
                          {r.report_error ? (
                            <span className="label label-warning abuseipdb-report-refused" title={r.report_error}>
                              Report refused
                            </span>
                          ) : null}
                        </td>
                        <td className="abuseipdb-nowrap">
                          {r.ticket_id ? <a href={`/app/easeedesk/ticket/${r.ticket_id}`}>Ticket #{r.ticket_id}</a> : null}
                          {r.ticket_id ? ' · ' : null}
                          <a href={`/app/securitee/incidents?device=${r.device_id}`}>Incidents</a>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </section>
  )
}
